/**
 * `POST /api/v1/reports` — the form that starts a generation job.
 *
 * ── Why all thirteen types are offered and eight are marked ─────────────────
 *
 * `wp_reports.report_type`'s CHECK accepts thirteen values; five have a builder and
 * the other eight are **refused** at `POST /reports` rather than generated empty.
 * Offering only five would make the other eight look as though the product never had
 * them; offering thirteen silently would teach people that a third of this form's own
 * options answer `400`. So each option says which it is, and the sentence beside the
 * select says how many.
 *
 * ── The request body ────────────────────────────────────────────────────────
 *
 * `ReportCreateRequest` is `additionalProperties: false` with four required members,
 * so every wrong key is a refusal naming a field the operator never typed —
 * `ExportSlot` shipped a body wrong in five places for exactly this reason, and the
 * lesson recorded there was that **a rendered string is not a check**. So the body is
 * built by `createRequestBody`, which is exported and compared against the schema in
 * `openapi.yaml` at test time rather than read off the DOM.
 *
 * An absent filter is an **omitted member**, never `null` and never `[]`: an empty
 * `employee_ids` is a different request from no `employee_ids`, and only the second
 * means "everybody in the caller's resolved scope".
 *
 * ── The one thing checked here, and why it is not a second authorisation path ──
 *
 * The period. Both dates are required by the schema and `period_start` after
 * `period_end` is a window with no days in it. Checking it here means the person
 * learns from the field rather than from a refusal; the server refuses regardless,
 * and nothing here decides whether the export happens.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { REPORTS_PATH } from '../../api/paths';
import type {
  Report,
  ReportCreateRequest,
  ReportFormatName,
  ReportTypeName,
} from '../../api/types';
import { useApiMutation } from '../../api/useApiMutation';
import { hasBuilder, REPORT_FORMATS, REPORT_TYPES } from '../../reports/catalog';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface CreateReportFormProps {
  /** Called with the `202` body, which is a complete `Report`. */
  readonly onCreated: (report: Report) => void;
}

export interface CreateDraft {
  readonly reportType: ReportTypeName;
  readonly format: ReportFormatName;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly employeeIds: string;
  readonly departmentId: string;
}

/** Split on commas and whitespace, de-duplicated, order preserved. See `schedule.ts`. */
export function parseIds(raw: string): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,]+/)) {
    const id = part.trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Both dates present, and the window is not inside out. */
export function periodIsUsable(draft: CreateDraft): boolean {
  if (draft.periodStart === '' || draft.periodEnd === '') return false;
  return draft.periodStart <= draft.periodEnd;
}

/**
 * `ReportCreateRequest`, built from the form.
 *
 * Exported so `reports-request.test.ts` can compare its **keys** against the schema
 * read out of `openapi.yaml`, in both directions — the technique
 * `export-request.test.ts` adopted after `ExportSlot` shipped a body the endpoint
 * refused outright. The next required member added to that schema fails here on the
 * day it is added.
 */
export function createRequestBody(draft: CreateDraft): ReportCreateRequest {
  const employees = parseIds(draft.employeeIds);
  const department = draft.departmentId.trim();
  return {
    report_type: draft.reportType,
    format: draft.format,
    period_start: draft.periodStart,
    period_end: draft.periodEnd,
    ...(employees.length === 0 ? {} : { employee_ids: employees }),
    ...(department === '' ? {} : { department_id: department }),
  };
}

export function CreateReportForm({ onCreated }: CreateReportFormProps): ReactNode {
  const { t } = useI18n();
  const [draft, setDraft] = useState<CreateDraft>({
    reportType: 'attendance',
    format: 'csv',
    periodStart: '',
    periodEnd: '',
    employeeIds: '',
    departmentId: '',
  });
  const create = useApiMutation<ReportCreateRequest, { data: Report }>(REPORTS_PATH);

  const usable = periodIsUsable(draft);
  const pending = create.status === 'pending';

  function change(patch: Partial<CreateDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function onSubmit(event: { preventDefault: () => void }): void {
    event.preventDefault();
    if (!usable || pending) return;
    void create.submit(createRequestBody(draft)).then(() => {
      // Read from the hook rather than from the resolved value: `submit` resolves
      // rather than rejects, so a refusal arrives here as `status === 'error'` with
      // the body already rendered by `ApiErrorNote`. Nothing is added to the list.
    });
  }

  // The `202` body is handed up in an effect rather than during render, because
  // `onCreated` sets state in the parent and React refuses that from a render pass.
  // `reset()` in the same effect is what makes it fire once per submit rather than on
  // every subsequent render: the hook's `data` is cleared, so the condition is false
  // next time round. Hand-checked dependency list — there is no `react-hooks` plugin
  // in this repository's eslint config (Task 53 §11 item 4) — and the check is that
  // the effect reads `create.status`, `create.data`, `create.reset` and `onCreated`
  // and nothing else.
  const createdEnvelope = create.status === 'ok' ? create.data : null;
  useEffect(() => {
    if (createdEnvelope === null) return;
    const { reset } = create;
    reset();
    onCreated(createdEnvelope.data);
  }, [createdEnvelope, create, onCreated]);

  return (
    <section className="wp-section" aria-labelledby="k8-create-heading" data-testid="k8-create">
      <h2 className="wp-section__title" id="k8-create-heading">
        {t('k8.reports.create.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="k8-create-builder-note">
        {t('k8.reports.create.builderNote')}
      </p>

      <form
        className="wp-filters wp-filters--bar"
        onSubmit={onSubmit}
        aria-labelledby="k8-create-heading"
      >
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-create-type">
            {t('k8.reports.create.type')}
          </label>
          <select
            className="wp-field__input"
            id="k8-create-type"
            data-testid="k8-create-type"
            value={draft.reportType}
            onChange={(event) => {
              change({ reportType: event.target.value as ReportTypeName });
            }}
          >
            {REPORT_TYPES.map((type) => (
              <option key={type} value={type} data-builder={hasBuilder(type) ? 'yes' : 'no'}>
                {t(`k8.reports.type.${type}` as MessageKey)}
              </option>
            ))}
          </select>
          {hasBuilder(draft.reportType) ? null : (
            <p className="wp-small" role="alert" data-testid="k8-create-unbuilt">
              {t('k8.reports.create.unbuilt')}
            </p>
          )}
          {draft.reportType === 'productivity' ? (
            <p className="wp-small wp-muted" data-testid="k8-create-stored-score">
              {t('k8.reports.create.productivityNote')}
            </p>
          ) : null}
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-create-format">
            {t('k8.reports.create.format')}
          </label>
          <select
            className="wp-field__input"
            id="k8-create-format"
            data-testid="k8-create-format"
            value={draft.format}
            onChange={(event) => {
              change({ format: event.target.value as ReportFormatName });
            }}
          >
            {REPORT_FORMATS.map((format) => (
              <option key={format} value={format}>
                {format}
              </option>
            ))}
          </select>
        </div>

        <DateField
          id="k8-create-from"
          testId="k8-create-from"
          label={t('k8.reports.create.from')}
          value={draft.periodStart}
          onChange={(periodStart) => {
            change({ periodStart });
          }}
        />

        <DateField
          id="k8-create-to"
          testId="k8-create-to"
          label={t('k8.reports.create.to')}
          value={draft.periodEnd}
          onChange={(periodEnd) => {
            change({ periodEnd });
          }}
        />

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-create-employees">
            {t('k8.reports.create.employees')}
          </label>
          <input
            className="wp-field__input"
            id="k8-create-employees"
            data-testid="k8-create-employees"
            type="text"
            autoComplete="off"
            value={draft.employeeIds}
            onChange={(event) => {
              change({ employeeIds: event.target.value });
            }}
          />
          <p className="wp-small wp-muted" data-testid="k8-create-employees-hint">
            {t('k8.reports.create.employeesHint')}
          </p>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-create-department">
            {t('k8.reports.create.department')}
          </label>
          <input
            className="wp-field__input"
            id="k8-create-department"
            data-testid="k8-create-department"
            type="text"
            autoComplete="off"
            value={draft.departmentId}
            onChange={(event) => {
              change({ departmentId: event.target.value });
            }}
          />
        </div>

        {usable ? null : (
          <p className="wp-small" role="status" data-testid="k8-create-period-invalid">
            {t('k8.reports.create.periodInvalid')}
          </p>
        )}

        {/* Disabled, and this is the honest kind: it says "you have not finished
            saying what you are asking for", which is a statement about the operator
            rather than about permission. Everything on this page that is about
            permission or about a state with no possible action is *absent* instead —
            `AccessReasonGate`'s docblock settles the distinction. */}
        <button
          className="wp-button wp-button--primary"
          type="submit"
          data-testid="k8-create-submit"
          disabled={!usable || pending}
        >
          {pending ? t('k8.reports.create.submitting') : t('k8.reports.create.submit')}
        </button>
      </form>

      {create.status === 'error' && create.error !== null ? (
        <ApiErrorNote error={create.error} subject={t('nav.reports')} testId="k8-create-error" />
      ) : null}
    </section>
  );
}
